/* eslint-disable @typescript-eslint/no-explicit-any */
import axios from 'axios'
import Cookies from 'js-cookie'
import qs from 'qs'

import { Env } from 'config/Env'
import { setCookie } from 'utils/cookieUtils'
import { getFingerprint } from 'utils/fingerprint'
import { clearPendoSession } from 'utils/pendoFunctions'

let gccInterceptor: number
export const setInterceptors = (contentType: string, token?: string) => {
  gccInterceptor = axios.interceptors.request.use(
    config => {
      config.headers = {
        ...{ Authorization: `Bearer ${token}` },
        'Content-Type': contentType,
      }
      return config
    },
    error => {
      if (!(error instanceof Error)) {
        const newError = new Error('An unknown error occurred')
        ;(newError as any).originalError = error
        return Promise.reject(newError)
      }
      return Promise.reject(error)
    },
  )
}

const removeInterceptor = (interceptor: number) => {
  axios.interceptors.request.eject(interceptor)
}

export const removeAuthorization = () => {
  axios.interceptors.request.eject(gccInterceptor)
}

const goToReload = () => {
  window.location.reload()
}

export const clearCache = () => {
  removeAuthorization()
  removeInterceptor(gccInterceptor)
  Cookies.remove('gcc_access_token')
  Cookies.remove('gcc_refresh_token')
  Cookies.remove('gcc_chat_token')
  Cookies.remove('chat_user_id')
  // Without this a stale 'sso' value survives into the next normal login and
  // routes its 401s to the IdP redirect instead of the refresh-token flow.
  Cookies.remove('login_type')
  clearPendoSession()
  // Drop the redux-persist snapshot so a stale permissionArray/userSessionToken
  // from a previous session can't trigger authenticated calls before the next
  // login has actually populated fresh state.
  localStorage.removeItem('persist:root')
  return 'Session removed'
}

export const ApiRequestTypes = {
  get: 'get',
  post: 'post',
  del: 'del',
  put: 'put',
  interceptors: 'interceptors',
  removeInterceptor: 'removeInterceptor',
  removeAuthorization: 'removeAuthorization',
}

// Get device fingerprint and store it for use in API requests

// Create Refresh token Form Data
const createRefreshTokenFormData = async () => {
  const fingerprint = await getFingerprint()
  return {
    grant_type: 'refresh_token',
    scope: 'openid offline_access referralscope',
    client_id: 'gccweb',
    client_secret: '?M[Qc;8&T^&UHyu',
    refresh_token: Cookies.get('gcc_refresh_token'),
    device_fingerprint: fingerprint,
    device_type: 3,
  }
}
setCookie('isAlreadyFetchingAccessToken', 'false')
let subscribers: Array<any> = []

function onAccessTokenFetched(access_token: string) {
  subscribers = subscribers.filter(callback => callback(access_token))
}

function addSubscriber(callback: any) {
  subscribers.push(callback)
}
const SSO_REDIRECT_URL = `${Env.SSO_CALLBACK_URL}callback`
const SSO_CLIENT_ID = `${Env.SSO_CLIENT_ID}`
const generateNonce = (): string => {
  const array = new Uint8Array(32)
  crypto.getRandomValues(array)
  return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('')
}

const redirectToIdp = () => {
  const nonce = generateNonce()
  sessionStorage.setItem('sso_nonce', nonce)

  const authUrl = new URL('https://auth.pac-iq.com/authorize')
  authUrl.searchParams.append('response_type', 'id_token')
  authUrl.searchParams.append('client_id', SSO_CLIENT_ID)
  authUrl.searchParams.append('redirect_uri', SSO_REDIRECT_URL)
  authUrl.searchParams.append('scope', 'openid profile email')
  authUrl.searchParams.append('nonce', nonce)
  window.location.href = authUrl.toString()
}
axios.interceptors.response.use(
  resp => resp,
  async error => {
    // No response = network error / CORS / timeout — pass through as-is so
    // axios.isAxiosError() still returns true in the caller's .catch handler.
    if (!error.response) return Promise.reject(error)

    const {
      config,
      response: { status },
    } = error
    const originalRequest = config

    if (status === 401) {
      const loginType = Cookies.get('login_type') // 'normal' or 'sso'
      const LOGIN_BASE_URL =
        loginType === 'sso'
          ? `${Env.REACT_APP_API_URL}SSO_VALIDATED`
          : `${Env.REACT_APP_API_URL}Identity/api/TOKEN/TOKEN`
      originalRequest.retryCount = originalRequest.retryCount || 0
      if (originalRequest.retryCount === undefined) {
        originalRequest.retryCount = 0
      }
      if (loginType === 'sso') {
        // eslint-disable-next-line no-plusplus
        originalRequest.retryCount++
        if (originalRequest.retryCount >= 3) {
          // STOP AFTER 3 FAILURES
          clearCache()
          goToReload()
          redirectToIdp()
          // A redirect is already in flight — never resolve/reject so callers'
          // .catch(showExceptionSnackbar) doesn't flash an error before the page unloads.
          return new Promise(() => {})
        }
        return axios(originalRequest)
      }

      if (Cookies.get('isAlreadyFetchingAccessToken') === 'false') {
        setCookie('isAlreadyFetchingAccessToken', 'true')
        removeInterceptor(gccInterceptor)
        removeAuthorization()
        setInterceptors('application/x-www-form-urlencoded', '')

        let resp
        try {
          const formData = await createRefreshTokenFormData()
          resp = await axios.post(LOGIN_BASE_URL, qs.stringify(formData))
        } catch (e) {
          console.error('Refresh token request failed', e)
        }

        if (resp?.status === 200 && resp?.data?.isSuccess) {
          setCookie('isAlreadyFetchingAccessToken', 'false')
          removeInterceptor(gccInterceptor)
          removeAuthorization()
          setInterceptors('application/json', resp.data.data.accessToken)

          setCookie('gcc_access_token', resp.data.data.accessToken)
          setCookie('gcc_refresh_token', resp.data.data.refreshToken)

          onAccessTokenFetched(resp.data.data.accessToken)
          return axios(originalRequest)
        }

        setCookie('isAlreadyFetchingAccessToken', 'false')
        clearCache()
        goToReload()
      } else if (originalRequest?.url === LOGIN_BASE_URL) {
        clearCache()
        goToReload()
      }

      const retryOriginalRequest = new Promise(resolve => {
        addSubscriber((access_token: string) => {
          originalRequest.headers.Authorization = `Bearer ${access_token}`
          resolve(axios(originalRequest))
        })
      })
      return retryOriginalRequest
    }
    return Promise.reject(error)
  },
)

const post = (url = '', data = {}, config = {}) =>
  axios.post(url, data, Object.assign(config, { timeout: 15 * 60 * 1000 }))

const get = (url = '', config = {}) => axios.get(url, config)

const put = (url = '', data = {}, config = {}) => axios.put(url, data, config)

const del = (url = '', config = {}) => axios.delete(url, config)

const HttpClient: any = {
  post,
  get,
  put,
  setInterceptors,
  del,
  removeAuthorization,
  removeInterceptor,
}

export { HttpClient }
